<template>
  <div class="sso-sites-card" :class="variantClass">
    <div class="card">
      <div class="card-body" :class="bodyClass">
        <!-- Header -->
        <div class="sso-sites-header" :class="{ 'text-center': isCentered }">
          <div class="d-flex align-items-center" :class="{ 'justify-content-center': isCentered }">
            <i class="ri-links-line icon-header mr-2"></i>
            <h5 class="mb-0">{{ title }}</h5>
          </div>
          <p v-if="showDescription" class="text-muted small mb-0 mt-2">
            {{ description }}
          </p>
        </div>

        <!-- Sites Grid -->
        <div class="sso-sites-grid" :class="gridClass">
          <a 
            v-for="client in clients" 
            :key="client.id"
            :href="client.url"
            target="_blank"
            rel="noopener noreferrer"
            class="sso-site-item"
            :class="itemClass"
          >
            <div class="site-logo">
              <img :src="client.logo" :alt="client.name" />
            </div>
            <div class="site-info">
              <h6 class="site-name">{{ client.name }}</h6>
              <span class="site-url">
                <i class="ri-external-link-line mr-1"></i>
                {{ getDomain(client.url) }}
              </span>
            </div>
            <div class="site-arrow">
              <i class="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>

        <!-- Footer (optional) -->
        <div v-if="showFooter" class="sso-sites-footer text-center mt-3">
          <small class="text-muted">
            <i class="ri-globe-line mr-1"></i>
            Todo el ecosistema Carné Joven a tu alcance
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ssoConfig from "@/config/sso.config";

export default {
  name: "SSOSitesCard",
  
  props: {
    // Variant: 'compact', 'default', 'large'
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['compact', 'default', 'large'].includes(value)
    },
    
    // Custom title
    title: {
      type: String,
      default: 'Sitios conectados'
    },
    
    // Show description
    showDescription: {
      type: Boolean,
      default: true
    },
    
    // Custom description
    description: {
      type: String,
      default: 'Descubre todas las plataformas de nuestra red'
    },
    
    // Show footer
    showFooter: {
      type: Boolean,
      default: false
    },
    
    // Center content
    isCentered: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    clients() {
      return ssoConfig.clients.filter(client => client.show_in_profile) || [];
    },
    
    variantClass() {
      return `sso-sites-${this.variant}`;
    },
    
    bodyClass() {
      return {
        'p-2': this.variant === 'compact',
        'p-3': this.variant === 'default',
        'p-4': this.variant === 'large'
      };
    },
    
    gridClass() {
      return {
        'grid-compact': this.variant === 'compact',
        'grid-default': this.variant === 'default',
        'grid-large': this.variant === 'large'
      };
    },
    
    itemClass() {
      return {
        'item-compact': this.variant === 'compact',
        'item-default': this.variant === 'default',
        'item-large': this.variant === 'large'
      };
    }
  },
  
  methods: {
    getDomain(url) {
      try {
        const urlObj = new URL(url);
        return urlObj.hostname;
      } catch {
        return url;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.sso-sites-card {
  .card {
    border: 1px solid #e9ecef;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }
  }
}

// Header
.sso-sites-header {
  margin-bottom: 1rem;
  
  .icon-header {
    font-size: 1.5rem;
    color: #556ee6;
  }
  
  h5 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #495057;
  }
}

// Sites Grid
.sso-sites-grid {
  display: grid;
  gap: 0.75rem;
  
  &.grid-compact {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  &.grid-default {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 0.75rem;
  }
  
  &.grid-large {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1rem;
  }
}

// Site Item
.sso-site-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: #fff;
    border-color: #556ee6;
    box-shadow: 0 0.25rem 0.5rem rgba(85, 110, 230, 0.15);
    transform: translateY(-2px);
    
    .site-arrow {
      transform: translateX(5px);
    }
  }
  
  &.item-compact {
    padding: 0.5rem;
    
    .site-logo {
      width: 40px;
      height: 40px;
      margin-right: 0.75rem;
    }
    
    .site-name {
      font-size: 0.875rem;
    }
    
    .site-url {
      font-size: 0.75rem;
    }
  }
  
  &.item-default {
    padding: 0.75rem;
    
    .site-logo {
      width: 50px;
      height: 50px;
      margin-right: 1rem;
    }
    
    .site-name {
      font-size: 0.95rem;
    }
    
    .site-url {
      font-size: 0.8rem;
    }
  }
  
  &.item-large {
    padding: 1rem;
    
    .site-logo {
      width: 60px;
      height: 60px;
      margin-right: 1.25rem;
    }
    
    .site-name {
      font-size: 1.05rem;
    }
    
    .site-url {
      font-size: 0.85rem;
    }
  }
}

// Site Logo
.site-logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 0.25rem;
  }
}

// Site Info
.site-info {
  flex: 1;
  min-width: 0;
  
  .site-name {
    margin-bottom: 0.25rem;
    font-weight: 600;
    color: #495057;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .site-url {
    display: flex;
    align-items: center;
    color: #74788d;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    i {
      flex-shrink: 0;
    }
  }
}

// Site Arrow
.site-arrow {
  flex-shrink: 0;
  margin-left: 0.5rem;
  color: #556ee6;
  font-size: 1.25rem;
  transition: transform 0.2s ease;
  
  i {
    display: block;
  }
}

// Footer
.sso-sites-footer {
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;
  
  i {
    color: #34c38f;
  }
}

// Responsive
@media (max-width: 767.98px) {
  .sso-sites-grid {
    &.grid-default,
    &.grid-large {
      grid-template-columns: 1fr;
    }
  }
  
  .sso-site-item {
    &.item-large {
      padding: 0.75rem;
      
      .site-logo {
        width: 50px;
        height: 50px;
        margin-right: 1rem;
      }
    }
  }
}

// Variants
.sso-sites-compact {
  .sso-sites-header {
    margin-bottom: 0.75rem;
    
    h5 {
      font-size: 1rem;
    }
  }
}

.sso-sites-large {
  .sso-sites-header {
    margin-bottom: 1.5rem;
    
    h5 {
      font-size: 1.25rem;
    }
  }
}
</style>

